/**
 * The context of the role.
 * - `"global"` - The role is applicable across all teams.
 * - `"team"` - The role is applicable only in a team context.
 */
export type RoleContext = 'global' | 'team';

interface RoleMatch {
  roleId?: number;
  roleName?: string;
}

export interface Role<TKey extends string, TMatch extends RoleMatch = {}> {
  type: 'role';
  key: TKey;
  label: string;
  description?: string;
  match: TMatch;
  context: RoleContext;
}

interface RoleOptions<TMatch extends RoleMatch> {
  label: string;
  description?: string;
  match: TMatch;
  /** @default "global" */
  context?: RoleContext;
}

export function defineRole<TKey extends string, TMatch extends RoleMatch = {}>(
  key: TKey,
  role: RoleOptions<TMatch>
): Readonly<Role<TKey, TMatch>> {
  return {
    type: 'role',
    key,
    label: role.label,
    match: role.match,
    description: role.description,
    context: role.context ?? 'global',
  };
}

export interface RoleGroup<TKey extends string> {
  type: 'group';
  key: TKey;
  roles: Role<any>[];
}

export function defineRoleGroup<TKey extends string>(
  key: TKey,
  roles: Role<any>[]
): Readonly<RoleGroup<TKey>> {
  return {
    type: 'group',
    key,
    roles,
  };
}

export interface UserMatchPartial {
  roleId?: number;
  roleName?: string;
}
